import { useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import merchantsApi from 'services/merchants';
import { PayoutDestinationType } from 'services/merchants/types';

import selectAccount from 'state/selectors/accounts/accountSelector';
import selectMerchant from 'state/selectors/merchants/merchantSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { ReduxToolkitError, SuccessResponse } from 'types/http';
import { UserAccessLevel } from 'types/user';

import { KEYS } from './keys';
import { Settlement } from './settlement';
import { DeleteEntityProps, transformPayoutDestinationsListDataToTableFormat } from './utils';

export const SettlementContainer = () => {
  const match = useMatch(KEYS.ROUTE_MATCH);
  const { account } = useSelector(selectAccount);
  const userLevel = useSelector(selectUserLevel);
  const { merchant } = useSelector(selectMerchant);
  const isPartner = Boolean(userLevel && userLevel === UserAccessLevel.Partner);
  const { uuid } = match?.params ?? { uuid: account.uuid };

  const [
    deletePaymentDestination,
    { isLoading: isDeletePaymentDestinationLoading, error: deletePaymentDestinationError, reset: resetDeletePaymentDestination },
  ] = merchantsApi.useDeletePayoutDestinationMutation();
  const deletePayoutDestinationProps: DeleteEntityProps<[string, PayoutDestinationType]> = {
    isDeleting: isDeletePaymentDestinationLoading,
    deleteError: deletePaymentDestinationError,
    deleteEntity: (uuid: string, type: PayoutDestinationType) =>
      deletePaymentDestination({
        merchantUuid: merchant.uuid,
        payoutDestinationUuid: uuid,
        payoutDestinationType: type,
      }) as Promise<ReduxToolkitError | SuccessResponse>,
    resetDelete: resetDeletePaymentDestination,
  };

  const { data: payoutDestinations, isFetching: isPayoutDestinationsFetching } = merchantsApi.useGetMerchantsPayoutDestinationsQuery(
    uuid!,
    {
      skip: !uuid,
    },
  );
  const { kyc } = merchant;

  const paymentDestinationsTableData = transformPayoutDestinationsListDataToTableFormat(
    deletePayoutDestinationProps,
    payoutDestinations?.data,
  );

  const merchantPayoutFlow = merchant.payoutFlow;

  return (
    <Settlement
      payoutMemo={merchant.payoutMemo ?? null}
      isPartner={isPartner}
      merchant={merchant}
      loading={isPayoutDestinationsFetching}
      payoutDestinations={paymentDestinationsTableData}
      kyc={kyc}
      merchantPayoutFlow={merchantPayoutFlow}
    />
  );
};
